/**
 * useRefillReturnToast — closes the loop on the ju refill flow.
 *
 * ADR-0108 phase 3.
 *
 * After clicking "Refill ju" (the canonical CTA from
 * `RefillJuButton`), the user leaves for Stripe Checkout. ADR-0330
 * Phase A keeps `success_url` / `cancel_url` on the originating
 * Planet / /jub origin (not `www.jubit.ai`). On their way back the
 * destination tacks on a `?refill=success` or `?refill=cancelled`
 * query so we know how to receive them.
 *
 * Without a return-handler the user comes back to the same page they
 * left (`/profile`, /planet, /jub, …). Their balance might have changed
 * (success path) but the UI doesn't refresh; their refill might have
 * been cancelled but they get no acknowledgement.
 *
 * What this hook does:
 *   1. On mount + on history change, parse the URL for `?refill=`
 *   2. If `success` → fire `invalidateCreditBalanceCache()` so the
 *      next render shows the new balance, return a `success` state
 *      with a toast message
 *   3. If `cancelled` → return `cancelled` state with a toast
 *      message acknowledging no charges
 *   4. Strip the `?refill=` param from the URL via
 *      `history.replaceState` so a refresh doesn't re-fire the toast
 *   5. Auto-clear the toast after 4 seconds (success) / 3 seconds
 *      (cancelled) — leave time to read the success copy with a
 *      potential balance number
 *
 * Server-side note: the success contract assumes the Phase-2 Stripe
 * webhook has already mutated `public.user_balance` BEFORE Stripe
 * redirects. If the webhook lags, `invalidateCreditBalanceCache`
 * will refetch but might still pull stale data. Phase-4 PR (live
 * wiring) should add a small `setTimeout(2_000, refresh)` retry to
 * cover the typical webhook latency window.
 */

import { useEffect, useState } from 'react';
import { trackEvent } from '../lib/analytics/track.ts';
import { invalidateCreditBalanceCache, useCreditBalance } from './index.ts';

export type RefillReturnState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'success'; readonly message: string }
  | { readonly kind: 'cancelled'; readonly message: string };

const SUCCESS_TOAST_MS = 4_000;
const CANCEL_TOAST_MS = 3_000;

/**
 * Pure-fn — read the `?refill=` query param from a URL string.
 * Exported for testing without spinning up jsdom history.
 */
export function parseRefillStatus(
  search: string,
): 'success' | 'cancelled' | null {
  if (!search) return null;
  const params = new URLSearchParams(search);
  const v = params.get('refill');
  if (v === 'success' || v === 'cancelled') return v;
  return null;
}

/**
 * Pure-fn — strip `?refill=...` from a URL string while preserving
 * everything else (path + other query keys + hash). Exported for
 * testing.
 *
 * Returns the input unchanged if it doesn't contain `?refill=` or if
 * URL parsing fails (malformed inputs round-trip safely instead of
 * being mangled by URL's lenient parser).
 */
export function stripRefillParam(href: string): string {
  if (!href.includes('refill=')) return href;
  try {
    // Fully-qualified URL path — preserve absolute origin if present.
    if (/^https?:\/\//i.test(href)) {
      const url = new URL(href);
      url.searchParams.delete('refill');
      const search = url.searchParams.toString();
      return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
    }
    // Path-relative URL — use placeholder origin solely to leverage
    // URLSearchParams. Strip the placeholder when emitting.
    const url = new URL(href, 'https://placeholder.invalid');
    url.searchParams.delete('refill');
    const search = url.searchParams.toString();
    return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
  } catch {
    return href;
  }
}

/**
 * Compose the toast message for the success path. Exported so the
 * caller can reuse it in inline UI without re-deriving copy.
 */
export function refillSuccessMessage(creditsAvailable: number): string {
  // Don't include a numeric delta — the webhook race makes that
  // unreliable. The new total is the honest signal.
  return `✓ Refilled — your ju balance is now ${creditsAvailable.toLocaleString()}.`;
}

export function refillCancelledMessage(): string {
  return '✕ Refill cancelled — no charges made.';
}

export function useRefillReturnToast(): RefillReturnState {
  const [state, setState] = useState<RefillReturnState>({ kind: 'idle' });
  // Subscribe to balance so the success message reads the latest
  // after invalidation. The `useCreditBalance` hook already debounces
  // its refetch.
  const { balance } = useCreditBalance();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const status = parseRefillStatus(window.location.search);
    if (status === null) return;

    // Strip the param so refresh / history-back doesn't re-fire.
    const stripped = stripRefillParam(window.location.href);
    window.history.replaceState({}, document.title, stripped);

    if (status === 'success') {
      // Refetch the balance from Supabase. The user_balance row was
      // updated by the Stripe webhook (phase 2); cache invalidation
      // is the trigger that gets it back to the client.
      invalidateCreditBalanceCache();
      trackEvent('credit_refill_returned', {
        surface: 'refill-return',
        extra: { kind: 'success' },
      });
      setState({
        kind: 'success',
        // Use the CURRENT balance value — by the time the toast
        // re-renders after the cache invalidation lands, this will
        // be the post-refill total.
        message: refillSuccessMessage(balance.creditsAvailable),
      });
      const t = window.setTimeout(
        () => setState({ kind: 'idle' }),
        SUCCESS_TOAST_MS,
      );
      return () => window.clearTimeout(t);
    }

    setState({
      kind: 'cancelled',
      message: refillCancelledMessage(),
    });
    trackEvent('credit_refill_returned', {
      surface: 'refill-return',
      extra: { kind: 'cancelled' },
    });
    const t = window.setTimeout(
      () => setState({ kind: 'idle' }),
      CANCEL_TOAST_MS,
    );
    return () => window.clearTimeout(t);
    // Initial-mount only. Subsequent navigation that ADDS the param
    // (rare; would happen if user clicked a `?refill=success` link
    // mid-session) is ignored — same-tab redirect-back is the
    // happy path we're modelling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
